import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ring-offset-background',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        success: 'bg-success text-success-foreground shadow hover:bg-success/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        subtle: 'bg-muted text-muted-foreground hover:bg-muted/80',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

function parseLegacyButtonClasses(className?: string) {
  if (!className) {
    return { variant: undefined as VariantProps<typeof buttonVariants>['variant'], size: undefined as VariantProps<typeof buttonVariants>['size'], cleaned: undefined as string | undefined };
  }
  const tokens = className.split(/\s+/).filter(Boolean);
  let variant: VariantProps<typeof buttonVariants>['variant'] | undefined;
  let size: VariantProps<typeof buttonVariants>['size'] | undefined;
  const remaining: string[] = [];
  for (const token of tokens) {
    if (!variant) {
      if (token === 'btn-primary') {
        variant = 'primary';
        continue;
      }
      if (token === 'btn-secondary' || token === 'btn') {
        variant = 'secondary';
        continue;
      }
      if (token === 'btn-ghost') {
        variant = 'ghost';
        continue;
      }
      if (token === 'btn-success') {
        variant = 'success';
        continue;
      }
      if (token === 'btn-danger') {
        variant = 'destructive';
        continue;
      }
    }
    if (!size) {
      if (token === 'btn-sm') {
        size = 'sm';
        continue;
      }
      if (token === 'btn-lg') {
        size = 'lg';
        continue;
      }
      if (token === 'btn-icon') {
        size = 'icon';
        continue;
      }
    }
    if (!token.startsWith('btn')) {
      remaining.push(token);
    }
  }
  return {
    variant,
    size,
    cleaned: remaining.length ? remaining.join(' ') : undefined,
  };
}

const LegacyButton = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { className, variant, size, ...rest } = props;
  const legacy = parseLegacyButtonClasses(typeof className === 'string' ? className : undefined);
  const resolvedVariant = variant ?? legacy.variant ?? 'secondary';
  const resolvedSize = size ?? legacy.size ?? 'default';
  const cleanedClassName = legacy.cleaned ?? (typeof className === 'string' ? undefined : className);
  return (
    <Button
      ref={ref}
      variant={resolvedVariant}
      size={resolvedSize}
      className={typeof className === 'string' ? cleanedClassName : className}
      {...rest}
    />
  );
});
LegacyButton.displayName = 'LegacyButton';

export { Button, buttonVariants, LegacyButton };
